import { Form } from 'semantic-ui-react';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Button, Modal } from '~/components';
import { CompanyOwnershipDisplay } from '~/constants';
import { useCreateCompany } from '~/mutations/admin/useCreateCompany';
import { COMPANY_SUBTYPE_CODES, CompanySubtypeCode } from '~/types/company';

interface AdminUserOption {
  id: string;
  name: string;
}

const SUBTYPE_OPTIONS = COMPANY_SUBTYPE_CODES.map(code => ({
  key: code,
  text: code,
  value: code,
}));

const TIER_OPTIONS = [
  { key: 'A', text: 'A', value: 'A' },
  { key: 'B', text: 'B', value: 'B' },
  { key: 'C', text: 'C', value: 'C' },
];

interface CreateCompanyModalProps {
  closeModal: () => void;
  adminUsers: AdminUserOption[];
}

export default function CreateCompanyModal({ closeModal, adminUsers }: CreateCompanyModalProps) {
  const [name, setName] = useState('');
  const [companyKind, setCompanyKind] = useState<'investor' | 'developer'>('investor');
  const [subtypeCodes, setSubtypeCodes] = useState<CompanySubtypeCode[]>([]);
  const [ownerId, setOwnerId] = useState<string | undefined>(undefined);
  const [thirdPartyExperience, setThirdPartyExperience] = useState<string | undefined>(undefined);

  const { mutateAsync, isLoading } = useCreateCompany();

  const leadOptions = adminUsers.map(u => ({
    key: u.id,
    text: u.name,
    value: u.id,
  }));

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Company name is required.');
      return;
    }

    try {
      await mutateAsync({
        name: trimmed,
        companyKind,
        ownerId: ownerId || undefined,
        thirdPartyExperience: thirdPartyExperience || undefined,
        subtypeCodes: subtypeCodes.length > 0 ? subtypeCodes : undefined,
      });
      toast.success('Company created.');
      closeModal();
    } catch (e: unknown) {
      console.error(e);
      toast.error('Could not create company. Please try again.');
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      closeModal();
    }
  };

  return (
    <Modal closeModal={handleClose} width='36rem'>
      <Modal.Title>New company</Modal.Title>
      <Modal.Content>
        <Form id='create-company-form' onSubmit={e => e.preventDefault()}>
          <Form.Field required>
            <label>Company name</label>
            <Form.Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder='Acme Solar'
              autoFocus
            />
          </Form.Field>
          <Form.Field required>
            <label>Organization</label>
            <Form.Group inline>
              <Form.Radio
                label='Investor'
                value='investor'
                checked={companyKind === 'investor'}
                onChange={() => setCompanyKind('investor')}
              />
              <Form.Radio
                label='Developer (installer)'
                value='developer'
                checked={companyKind === 'developer'}
                onChange={() => setCompanyKind('developer')}
              />
            </Form.Group>
          </Form.Field>
          <Form.Field>
            <label>{CompanyOwnershipDisplay.THIRD_PARTY_EXPERIENCE}</label>
            <Form.Dropdown
              fluid
              selection
              clearable
              placeholder='Optional'
              options={TIER_OPTIONS}
              value={thirdPartyExperience}
              onChange={(_, { value }) => setThirdPartyExperience(value as string | undefined)}
            />
          </Form.Field>
          <Form.Field>
            <label>Subtypes</label>
            <Form.Dropdown
              fluid
              multiple
              selection
              clearable
              placeholder='Optional'
              options={SUBTYPE_OPTIONS}
              value={subtypeCodes}
              onChange={(_, { value }) => setSubtypeCodes((value as CompanySubtypeCode[]) ?? [])}
            />
          </Form.Field>
          <Form.Field>
            <label>Conductor lead</label>
            <Form.Dropdown
              fluid
              selection
              clearable
              placeholder='Optional'
              options={leadOptions}
              value={ownerId}
              onChange={(_, { value }) => setOwnerId(value as string | undefined)}
              disabled={leadOptions.length === 0}
            />
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Footer>
        <Button primary outlined onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button primary loading={isLoading} disabled={isLoading} onClick={handleSubmit}>
          Create company
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
